<template>
  <base-card-v2 :class="{ loadHeight: loading }" class="category-historic">
    <div v-show="$_verifyLoaded('done')">
      <base-card-header :title="$t('categories.orders.category-historic')" />
      <div id="chart-category-historic" style="min-width: 360px; height: 400px; margin: 0 auto">
        {{ $t('loading-dots') }}
      </div>
    </div>
    <base-loader v-show="$_verifyLoaded('loading')" />
    <info-card v-show="$_verifyLoaded('info')" ref="message-error" :error="hasError" />
  </base-card-v2>
</template>

<script>
/* eslint-disable func-names */
import bus from '@/helpers/events/bus';
import Highcharts from 'highcharts';
import { getCategoryLifecycle } from '@/services/categories';
import BaseLoader from '@/components/_atoms/BaseLoaderSpinner';
import InfoCard from '@/components/_atoms/InfoCard';
import verifyMixin from '@/helpers/mixins/verifyMixin';
import { formatDecimal, formatCurrency } from '@/utilities/formatters';

export default {
  components: {
    BaseLoader,
    InfoCard,
  },
  mixins: [verifyMixin],
  props: {
    category: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      chartData: {},
    };
  },
  created() {
    this.fetchData();
    bus.$on('render-cards', () => {
      this.fetchData();
    });
  },
  methods: {
    fetchData() {
      this.$_reqConfig();

      getCategoryLifecycle(this.category.toUpperCase())
        .then(({ data }) => {
          if (this.$_verifyData(data.data)) return;

          this.chartData = data.data;
        })
        .then(() => {
          this.mountFixedPlacementColumns();
        })
        .catch(() => {
          this.hasError = true;
          this.$_componentHeight();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    mountFixedPlacementColumns() {
      Highcharts.addEvent(Highcharts.Series, 'afterInit', function () {
        this.symbolUnicode =
          {
            circle: '●',
            line: '━',
          }[this.symbol] || '●';
      });

      Highcharts.chart('chart-category-historic', {
        title: {
          text: '',
        },
        legend: {
          enabled: true,
          align: 'left',
          verticalAlign: 'top',
          itemMarginTop: -30,
          itemMarginBottom: 25,
          itemStyle: {
            color: '#555',
            fontWeight: 600,
          },
        },
        credits: {
          enabled: false,
        },
        chart: {
          type: 'column',
          style: {
            fontFamily: 'Montserrat',
          },
        },
        xAxis: {
          categories: this.chartData.categories,
        },
        yAxis: [
          {
            title: {
              text: null,
            },
            lineWidth: 0,
            labels: {
              formatter: function formatter() {
                return formatDecimal(this.value);
              },
            },
          },
          {
            title: {
              text: null,
            },
            labels: {
              formatter: function formatter() {
                return formatCurrency(this.value);
              },
            },
            lineWidth: 0,
            opposite: true,
          },
        ],
        tooltip: {
          shared: true,
          useHTML: true,
          valueDecimals: 0,
          borderWidth: 0,
          backgroundColor: '#fff',
          formatter: function formater() {
            const htmlTooltip = this.points.map(x => {
              let formattedValue = String;
              if (x.series.index === 0 || x.series.index === 1) {
                formattedValue = formatDecimal(x.y);
              } else {
                formattedValue = formatCurrency(x.y);
              }
              return `<span style="color:${x.series.color}">${x.series.symbolUnicode}</span> ${x.series.name}: <b>${formattedValue}</b></br>`;
            });
            return htmlTooltip;
          },
          style: {
            color: '#555',
          },
        },
        plotOptions: {
          series: { marker: { enabled: true } },
          spline: {
            marker: {
              enabled: false,
              shadow: false,
            },
            lineWidth: 3,
          },
          column: {
            grouping: false,
            shadow: false,
            borderWidth: 0,
          },
        },
        series: [
          {
            name: this.$i18n.t('categories.orders.average_quantity'),
            color: '#365B6B20',
            data: this.chartData.series.average_quantity.data,
            pointPadding: -0.01,
          },
          {
            name: this.$i18n.t('categories.orders.category_quantity'),
            color: '#6C90F4',
            data: this.chartData.series.category_quantity.data,
            pointPadding: 0.2,
          },
          {
            name: this.$i18n.t('categories.orders.asp'),
            marker: { symbol: 'line' },
            color: '#DDDDDD',
            data: this.chartData.series.asp.data,
            type: 'spline',
            yAxis: 1,
          },
          {
            name: this.$i18n.t('categories.orders.asp_category'),
            marker: { symbol: 'line' },
            color: '#EEDA36',
            data: this.chartData.series.asp_category.data,
            type: 'spline',
            yAxis: 1,
          },
        ],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
#chart-category-historic {
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  color: #555;
}

.category-historic {
  &.loadHeight {
    height: 490px;
  }
}
</style>
